import React, { useState, useMemo } from "react";
import { base44 } from "@/race-tracker/api/rtClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/race-tracker/components/ui/button";
import { Plus, MapPin } from "lucide-react";
import { getSweepStaleness } from "@/race-tracker/lib/sweepUtils";
import FilterBar from "../components/filters/FilterBar";
import AdvertiserCard from "../components/cards/AdvertiserCard";
import RaceDetailModal from "../components/modals/RaceDetailModal";
import AdvertiserFormModal from "../components/modals/AdvertiserFormModal";
import AddRaceModal from "../components/modals/AddRaceModal";

export default function Dashboard() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    search: "", da: "all", ae: "all", am: "all", state: "all",
    chamber: "all", election_type: "all", status: "Active", smg_plus: false, agency: "all",
    sweep_staleness: "all",
  });
  const [selectedRace, setSelectedRace] = useState(null);
  const [editingAdvertiser, setEditingAdvertiser] = useState(null);
  const [showNewAdvertiser, setShowNewAdvertiser] = useState(false);
  const [addRaceFor, setAddRaceFor] = useState(null);

  const { data: advertisers = [], isLoading: loadingAds } = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => base44.entities.Advertiser.list("-created_date", 500),
  });

  const { data: races = [], isLoading: loadingRaces } = useQuery({
    queryKey: ["races"],
    queryFn: () => base44.entities.Race.list("-created_date", 5000),
  });

  const { data: opponents = [] } = useQuery({
    queryKey: ["opponents"],
    queryFn: () => base44.entities.Opponent.list("-created_date", 10000),
  });

  // Build opponent counts per race
  const opponentCounts = useMemo(() => {
    const counts = {};
    opponents.forEach((o) => {
      counts[o.race_id] = (counts[o.race_id] || 0) + 1;
    });
    return counts;
  }, [opponents]);

  // Group races by advertiser
  const racesByAdvertiser = useMemo(() => {
    const map = {};
    races.forEach((r) => {
      if (!map[r.advertiser_id]) map[r.advertiser_id] = [];
      map[r.advertiser_id].push({ ...r, _opponent_count: opponentCounts[r.id] || 0 });
    });
    return map;
  }, [races, opponentCounts]);

  // Apply filters
  const filtered = useMemo(() => {
    return advertisers.filter((ad) => {
      const adRaces = racesByAdvertiser[ad.id] || [];
      const search = filters.search?.toLowerCase();

      if (search) {
        const matchAd = ad.advertiser_name?.toLowerCase().includes(search) ||
          ad.candidate_name?.toLowerCase().includes(search);
        const matchRace = adRaces.some((r) =>
          r.state?.toLowerCase().includes(search) ||
          r.district?.toLowerCase().includes(search) ||
          r.chamber?.toLowerCase().includes(search)
        );
        if (!matchAd && !matchRace) return false;
      }

      if (filters.da !== "all" && ad.data_analyst !== filters.da) return false;
      if (filters.ae !== "all" && ad.account_executive !== filters.ae) return false;
      if (filters.am !== "all" && ad.account_manager !== filters.am) return false;
      if (filters.smg_plus && !ad.smg_plus) return false;
      if (filters.agency !== "all" && ad.agency !== filters.agency) return false;

      // Status filter — hide Done unless explicitly selected
      if (filters.status === "Active") {
        if (ad.status === "Done") return false;
      } else if (filters.status !== "all") {
        if (ad.status !== filters.status) {
          const hasMatchingRace = adRaces.some((r) => r.status === filters.status);
          if (!hasMatchingRace) return false;
        }
      }

      // Race-level filters
      if (filters.state !== "all" || filters.chamber !== "all" || filters.election_type !== "all") {
        const matchingRaces = adRaces.filter((r) => {
          if (filters.state !== "all" && r.state !== filters.state) return false;
          if (filters.chamber !== "all" && r.chamber !== filters.chamber) return false;
          if (filters.election_type !== "all" && r.election_type !== filters.election_type) return false;
          return true;
        });
        if (matchingRaces.length === 0) return false;
      }

      return true;
    });
  }, [advertisers, racesByAdvertiser, filters]);

  // Filter races for each advertiser based on race-level filters
  const STALENESS_THRESHOLD = { warning: 7, danger: 14, critical: 30 };

  const getFilteredRaces = (advertiserId) => {
    const adRaces = racesByAdvertiser[advertiserId] || [];
    return adRaces.filter((r) => {
      if (filters.status === "Active" && r.status === "Done") return false;
      if (filters.status !== "all" && filters.status !== "Active" && r.status !== filters.status) return false;
      if (filters.state !== "all" && r.state !== filters.state) return false;
      if (filters.chamber !== "all" && r.chamber !== filters.chamber) return false;
      if (filters.election_type !== "all" && r.election_type !== filters.election_type) return false;
      if (filters.sweep_staleness !== "all") {
        const threshold = STALENESS_THRESHOLD[filters.sweep_staleness];
        const { days } = getSweepStaleness(r.last_digital_sweep);
        if (days === null || days <= threshold) return false;
      }
      return true;
    });
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["advertisers"] });
    queryClient.invalidateQueries({ queryKey: ["races"] });
    queryClient.invalidateQueries({ queryKey: ["opponents"] });
  };

  const handleSaveAdvertiser = async (data, id) => {
    if (id) {
      // If setting to Done, cascade to all child races
      const existing = advertisers.find((a) => a.id === id);
      if (existing && existing.status !== "Done" && data.status === "Done") {
        const adRaces = racesByAdvertiser[id] || [];
        for (const r of adRaces) {
          if (r.status !== "Done") {
            await base44.entities.Race.update(r.id, { status: "Done" });
          }
        }
      }
      await base44.entities.Advertiser.update(id, data);
    } else {
      await base44.entities.Advertiser.create(data);
    }
    setEditingAdvertiser(null);
    setShowNewAdvertiser(false);
    refresh();
  };

  const handleDeleteAdvertiser = async (advertiserId) => {
    const adRaces = racesByAdvertiser[advertiserId] || [];
    for (const r of adRaces) {
      const raceOpponents = opponents.filter((o) => o.race_id === r.id);
      for (const o of raceOpponents) {
        await base44.entities.Opponent.delete(o.id);
      }
      await base44.entities.Race.delete(r.id);
    }
    await base44.entities.Advertiser.delete(advertiserId);
    setEditingAdvertiser(null);
    refresh();
  };

  const handleDeleteRace = async (raceId) => {
    // Delete opponents first
    const raceOpponents = opponents.filter((o) => o.race_id === raceId);
    for (const o of raceOpponents) {
      await base44.entities.Opponent.delete(o.id);
    }
    await base44.entities.Race.delete(raceId);
    setSelectedRace(null);
    refresh();
  };

  const isLoading = loadingAds || loadingRaces;

  const totalActiveRaces = races.filter((r) => r.status === "Active").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Competitive Intelligence</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {filtered.length} advertiser{filtered.length !== 1 ? "s" : ""} · {totalActiveRaces} active race{totalActiveRaces !== 1 ? "s" : ""}
          </p>
        </div>
        <Button size="sm" className="text-xs gap-1.5" style={{ backgroundColor: "hsl(var(--smg-accent-yellow))", color: "hsl(224 40% 14%)" }} onClick={() => setShowNewAdvertiser(true)}>
          <Plus className="w-3.5 h-3.5" /> New Advertiser
        </Button>
      </div>

      {/* Filters */}
      <FilterBar filters={filters} onChange={setFilters} />

      {/* Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MapPin className="w-8 h-8 text-muted-foreground/50 mb-3" />
          <p className="text-sm font-medium text-muted-foreground">No advertisers match your filters</p>
          <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters or add a new advertiser</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((ad) => (
            <AdvertiserCard
              key={ad.id}
              advertiser={ad}
              races={getFilteredRaces(ad.id)}
              onRaceClick={setSelectedRace}
              onEditAdvertiser={setEditingAdvertiser}
              onAddRace={setAddRaceFor}
            />
          ))}
        </div>
      )}

      {/* Floating Add Race Button (shown per advertiser via card) */}

      {/* Modals */}
      {selectedRace && (
        <RaceDetailModal
          race={selectedRace}
          open={!!selectedRace}
          onClose={() => setSelectedRace(null)}
          onSave={() => { setSelectedRace(null); refresh(); }}
          onDelete={handleDeleteRace}
        />
      )}

      <AdvertiserFormModal
        advertiser={editingAdvertiser}
        open={!!editingAdvertiser || showNewAdvertiser}
        onClose={() => { setEditingAdvertiser(null); setShowNewAdvertiser(false); }}
        onSave={handleSaveAdvertiser}
        onDelete={handleDeleteAdvertiser}
      />

      {addRaceFor && (
        <AddRaceModal
          advertiserId={addRaceFor}
          open={!!addRaceFor}
          onClose={() => setAddRaceFor(null)}
          onSave={() => { setAddRaceFor(null); refresh(); }}
        />
      )}
    </div>
  );
}